/**
 * W66.G — OrgNodeCard
 * Displays a single org node with edit and delete actions.
 */

import { useState } from "react";
import { Pencil, Trash2, Check, X } from "lucide-react";
import type { OrgNode, RoleInDeal, Sentiment } from "./types";
import { rolePillClass, sentimentColor, ROLES, SENTIMENTS } from "./types";

interface Props {
  node: OrgNode;
  canEdit: boolean;
  onSave: (id: number, patch: Partial<OrgNode>) => Promise<void>;
  onDelete: (id: number) => void;
}

export default function OrgNodeCard({ node, canEdit, onSave, onDelete }: Props) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  // Editable state
  const [name, setName] = useState(node.display_name);
  const [title, setTitle] = useState(node.title ?? "");
  const [role, setRole] = useState<RoleInDeal | "">(node.role_in_deal ?? "Unknown");
  const [power, setPower] = useState<number>(node.power_level ?? 3);
  const [sentiment, setSentiment] = useState<Sentiment | "">(node.sentiment ?? "Unknown");
  const [notes, setNotes] = useState(node.notes ?? "");

  function startEdit() {
    setName(node.display_name);
    setTitle(node.title ?? "");
    setRole(node.role_in_deal ?? "Unknown");
    setPower(node.power_level ?? 3);
    setSentiment(node.sentiment ?? "Unknown");
    setNotes(node.notes ?? "");
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
  }

  async function handleSave() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await onSave(node.id, {
        display_name: name.trim(),
        title: title.trim() || null,
        role_in_deal: (role as RoleInDeal) || "Unknown",
        power_level: power,
        sentiment: (sentiment as Sentiment) || "Unknown",
        notes: notes.trim() || null,
      });
      setEditing(false);
    } finally {
      setBusy(false);
    }
  }

  function handleDelete() {
    if (!window.confirm(`Remove "${node.display_name}" from the org map?`)) return;
    onDelete(node.id);
  }

  const roleCls = rolePillClass(node.role_in_deal);
  const sentCls = sentimentColor(node.sentiment);

  if (editing) {
    return (
      <div
        className="rounded border border-border bg-background p-4 space-y-3"
        data-testid={`org-node-edit-${node.id}`}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Name *</span>
            <input
              autoFocus
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full text-[13px] px-2.5 py-1.5 rounded border border-border bg-background focus:outline-none focus:border-foreground"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Title</span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1 w-full text-[13px] px-2.5 py-1.5 rounded border border-border bg-background focus:outline-none focus:border-foreground"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Role in Deal</span>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as RoleInDeal)}
              className="mt-1 w-full text-[13px] px-2.5 py-1.5 rounded border border-border bg-background focus:outline-none focus:border-foreground"
            >
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Sentiment</span>
            <select
              value={sentiment}
              onChange={(e) => setSentiment(e.target.value as Sentiment)}
              className="mt-1 w-full text-[13px] px-2.5 py-1.5 rounded border border-border bg-background focus:outline-none focus:border-foreground"
            >
              {SENTIMENTS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Power Level (1–5)</span>
            <input
              type="number"
              min={1}
              max={5}
              value={power}
              onChange={(e) => setPower(Math.max(1, Math.min(5, Number(e.target.value))))}
              className="mt-1 w-full text-[13px] px-2.5 py-1.5 rounded border border-border bg-background focus:outline-none focus:border-foreground"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Notes</span>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1 w-full text-[13px] px-2.5 py-1.5 rounded border border-border bg-background focus:outline-none focus:border-foreground"
            />
          </label>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={busy || !name.trim()}
            className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded border bg-foreground text-background border-foreground hover:opacity-90 disabled:opacity-50 transition"
          >
            <Check className="w-3.5 h-3.5" /> {busy ? "Saving..." : "Save"}
          </button>
          <button
            type="button"
            onClick={cancelEdit}
            disabled={busy}
            className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded border border-border bg-background hover:bg-[hsl(var(--surface-2))] transition"
          >
            <X className="w-3.5 h-3.5" /> Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded border border-border bg-[hsl(var(--surface))] p-4"
      data-testid={`org-node-${node.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          {/* Name + title */}
          <div className="font-semibold text-[14px] tracking-tight truncate">
            {node.display_name}
          </div>
          {node.title && (
            <div className="text-[12px] text-muted-foreground mt-0.5 truncate">{node.title}</div>
          )}

          {/* Role pill */}
          <div className="mt-2 flex items-center flex-wrap gap-2">
            {node.role_in_deal && (
              <span
                className={`inline-block text-[10px] font-medium px-2 py-0.5 rounded-full ${roleCls}`}
                data-testid={`node-role-${node.id}`}
              >
                {node.role_in_deal}
              </span>
            )}
          </div>

          {/* Power dots + sentiment bar row */}
          <div className="mt-2 flex items-center gap-4">
            {/* Power level dots */}
            <div className="flex items-center gap-1" title={`Power level: ${node.power_level ?? "—"}/5`}>
              {[1, 2, 3, 4, 5].map((n) => (
                <span
                  key={n}
                  className={`w-2 h-2 rounded-full border ${
                    node.power_level && n <= node.power_level
                      ? "bg-foreground border-foreground"
                      : "bg-transparent border-border"
                  }`}
                />
              ))}
              <span className="text-[10px] text-muted-foreground ml-1">
                {node.power_level ?? "—"}/5
              </span>
            </div>

            {/* Sentiment bar */}
            {node.sentiment && (
              <div className="flex items-center gap-1.5">
                <div className="w-16 h-1.5 rounded-full bg-border overflow-hidden">
                  <div
                    className={`h-full rounded-full ${sentCls}`}
                    style={{
                      width:
                        node.sentiment === "Supporter"
                          ? "100%"
                          : node.sentiment === "Neutral"
                          ? "50%"
                          : node.sentiment === "Skeptic"
                          ? "33%"
                          : node.sentiment === "Blocker"
                          ? "16%"
                          : "0%",
                    }}
                  />
                </div>
                <span className="text-[10px] text-muted-foreground">{node.sentiment}</span>
              </div>
            )}
          </div>

          {/* Notes */}
          {node.notes && (
            <p className="mt-2 text-[11px] text-muted-foreground italic truncate">{node.notes}</p>
          )}
        </div>

        {/* Actions */}
        {canEdit && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={startEdit}
              className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-border bg-background hover:bg-[hsl(var(--surface-2))] transition"
              aria-label="Edit"
              data-testid={`org-edit-${node.id}`}
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={handleDelete}
              className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-rose-200 text-rose-700 bg-rose-50/50 hover:bg-rose-50 transition"
              aria-label="Delete"
              data-testid={`org-delete-${node.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
